import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { subscriptionPlans } from '../data/subscriptionPlans';
import { CreditCard, Check, ArrowRight, Star, Loader2, Tag, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SubscriptionPage = () => {
  const { user, isAuthenticated, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(user?.subscriptionTier || 'free');
  const [billingHistory, setBillingHistory] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  const currentPlan = subscriptionPlans.find(p => p.id === (user?.subscriptionTier || 'free'));

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch fresh user data on page load
  useEffect(() => {
    const fetchFreshUserData = async () => {
      if (user?.id) {
        try {
          const response = await axios.get(`${API_URL}/api/users/${user.id}`);
          // Update user data in localStorage and context
          login(response.data);
        } catch (error) {
          console.error('Error fetching fresh user data:', error);
        }
      }
    };

    fetchFreshUserData();
  }, []); // Run once on mount

  // Handle return from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && !processingPayment) {
      handlePaymentReturn(sessionId);
    }
  }, [searchParams]);

  const handlePaymentReturn = async (sessionId) => {
    setProcessingPayment(true);
    
    toast({
      title: 'Processing Payment...',
      description: 'Please wait while we verify your payment.',
    });

    // Poll payment status
    const maxAttempts = 5;
    let attempts = 0;
    
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`);
        const data = response.data;
        
        if (data.paymentStatus === 'paid') {
          // Refresh user data
          const userResponse = await axios.post(`${API_URL}/api/auth/login`, {
            email: user.email,
            password: '' // We'll need to handle this differently in production
          });
          
          toast({
            title: 'Payment Successful!',
            description: 'Your subscription has been upgraded successfully.',
          });
          
          setProcessingPayment(false);
          // Redirect professionals to invoice page after payment
          if (user?.userType === 'professional') {
            window.location.href = '/pro/invoices';
          } else {
            window.location.href = '/subscription';
          }
          return;
        } else if (data.status === 'expired') {
          toast({
            title: 'Payment Expired',
            description: 'The payment session expired. Please try again.',
            variant: 'destructive',
          });
          setProcessingPayment(false);
          return;
        }
        
        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          toast({
            title: 'Payment Processing',
            description: 'Payment is taking longer than expected. Please check back later.',
          });
          setProcessingPayment(false);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        toast({
          title: 'Error',
          description: 'Failed to verify payment. Please contact support.',
          variant: 'destructive',
        });
        setProcessingPayment(false);
      }
    };
    
    pollStatus();
  };


  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoDiscount(null);
      return;
    }

    setValidatingPromo(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/promo-codes/validate`, {
        code: promoCode.toUpperCase(),
        subscriptionTier: selectedPlan
      });
      
      setPromoDiscount(response.data);
      toast({
        title: 'Promo Code Applied!',
        description: `You saved $${response.data.discountAmount.toFixed(2)}`,
      });
    } catch (error) {
      setPromoDiscount(null);
      toast({
        title: 'Invalid Promo Code',
        description: error.response?.data?.detail || 'This promo code is not valid',
        variant: 'destructive',
      });
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleUpgrade = async (plan) => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API_URL}/api/payments/checkout/session`, {
        packageId: plan.id,
        originUrl: originUrl,
        userId: user.id,
        email: user.email,
        promoCode: promoDiscount ? promoDiscount.code : null
      });
      
      // Check if it's a free subscription (100% discount)
      if (response.data.free_subscription) {
        // Force a full page reload to update all user data
        toast({
          title: 'Success!',
          description: 'Your subscription has been activated!',
        });
        // Wait a moment then reload to show updated subscription
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        return;
      }
      
      // Redirect to Stripe checkout for paid subscriptions
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Failed to initiate payment. Please try again.',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Show professional-only message for non-professionals
  if (user?.userType !== 'professional') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Account Required</h2>
          <p className="text-gray-600 mb-6">Subscription management is only available for professional accounts.</p>
          <Button onClick={() => navigate('/')} className="bg-orange-600 hover:bg-orange-700 text-white">
            Back to Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Subscription & Billing</h1>
          <p className="text-gray-600 mt-2">Manage your subscription plan and payment methods</p>
        </div>

        {processingPayment && (
          <Card className="mb-8 border-2 border-blue-500 bg-blue-50">
            <CardContent className="py-6">
              <div className="flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-3" />
                <span className="text-lg font-medium text-blue-900">Processing your payment...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Plan */}
        <Card className="mb-8 border-2 border-orange-500">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Current Plan</span>
              <Badge className="bg-orange-600 text-white">Active</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentPlan?.name}</h3>
                <p className="text-gray-600 mb-2">{currentPlan?.description}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">${currentPlan?.price}</span>
                  <span className="text-gray-600">/{currentPlan?.billingPeriod}</span>
                  {currentPlan?.monthlyEquivalent && (
                    <span className="text-sm text-gray-500">(${currentPlan?.monthlyEquivalent}/mo)</span>
                  )}
                </div>
                <div className="mt-2">
                  <span className="text-sm text-gray-600">Lead Cost: </span>
                  <span className="text-lg font-bold text-orange-600">${currentPlan?.leadCost}/lead</span>
                </div>
              </div>
              {currentPlan?.id !== 'premium' && (
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-2">Want to upgrade?</p>
                  <p className="text-xs text-gray-500 mb-3">Select a plan below to checkout</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Available Plans */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subscriptionPlans.map((plan) => (
              <Card 
                key={plan.id}
                className={`${
                  plan.id === selectedPlan
                    ? 'border-2 border-orange-500 shadow-lg' 
                    : plan.id === currentPlan?.id
                    ? 'border-2 border-gray-400'
                    : 'border border-gray-200'
                } ${plan.popular ? 'relative' : ''} transition-all duration-200`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-orange-600 text-white">
                      <Star className="h-3 w-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="text-3xl font-bold text-gray-900 mt-2">
                    ${plan.price}
                    <span className="text-sm text-gray-600 font-normal">/{plan.billingPeriod}</span>
                  </div>
                  {plan.monthlyEquivalent && (
                    <p className="text-sm text-gray-500">${plan.monthlyEquivalent}/month</p>
                  )}
                </CardHeader>
                
                <CardContent>
                  <div className="bg-orange-50 rounded p-3 mb-4">
                    <p className="text-sm font-semibold">Lead Pricing</p>
                    <p className="text-2xl font-bold text-orange-600">${plan.leadCost}</p>
                    <p className="text-xs text-gray-600">per qualified lead</p>
                  </div>

                  <div className="space-y-2 mb-4">
                    {plan.features.slice(0, 5).map((feature, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {plan.id === currentPlan?.id ? (
                    <Button disabled className="w-full bg-gray-100">
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Current Plan
                    </Button>
                  ) : plan.id === 'free' ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled
                    >
                      Contact Support to Downgrade
                    </Button>
                  ) : (
                    <>
                      {selectedPlan === plan.id ? (
                        <Button
                          onClick={() => handleUpgrade(plan)}
                          disabled={loading || processingPayment}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="mr-2 h-4 w-4" />
                              Proceed to Checkout
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setSelectedPlan(plan.id)}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          Select {plan.name}
                        </Button>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Promo Code Section - Always Visible */}
        <Card className="mb-8 border-2 border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Tag className="h-5 w-5 mr-2 text-orange-600" />
              Promo Code (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && validatePromoCode()}
                  disabled={validatingPromo || selectedPlan === 'free'}
                />
              </div>
              <Button
                onClick={validatePromoCode}
                disabled={!promoCode.trim() || validatingPromo || selectedPlan === 'free'}
                variant="outline"
              >
                {validatingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
            
            {selectedPlan === 'free' && (
              <p className="text-sm text-gray-500 mt-2">
                Promo codes only apply to paid plans (Professional or Premium)
              </p>
            )}
            
            {promoDiscount && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-900">Promo Applied: {promoDiscount.code}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setPromoCode(''); setPromoDiscount(null); }}
                    className="text-green-700 hover:text-green-900"
                  >
                    Remove
                  </Button>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Price:</span>
                    <span className="text-gray-900">${promoDiscount.originalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-700 font-semibold">
                    <span>Discount:</span>
                    <span>-${promoDiscount.discountAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-green-200">
                    <span className="font-bold text-gray-900">Final Price:</span>
                    <span className="font-bold text-gray-900">
                      {promoDiscount.finalPrice === 0 ? 'FREE' : `$${promoDiscount.finalPrice.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="h-5 w-5 mr-2 text-orange-600" />
              Payment Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
              <div className="text-center">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">Payments processed securely via Stripe</p>
                <p className="text-sm text-gray-500">Payment methods are managed during checkout</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing History */}
        <Card>
          <CardHeader>
            <CardTitle>Billing History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <p>No billing history yet</p>
              <p className="text-sm mt-2">Your invoices will appear here after payment</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default SubscriptionPage;
